import React, { useState, useEffect } from 'react';

type AdminRequest = {
  request_id: string;
  reference_id: string;
  queue_status: string;
  reqtype: string;
  display_name: string | null;
  rejected_reason?: string;
};

interface RequestedItemsProps {
  selectedRequest: AdminRequest | null;
  onClose: () => void;
}

const REQUEST_DETAILS_API_URL = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/pharma/request';
const REQUEST_DELETE_API_BASE_URL = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/pharma/request';

const RequestedItems: React.FC<RequestedItemsProps> = ({ selectedRequest, onClose }) => {
  const [requestDetails, setRequestDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showProcessingAnimation, setShowProcessingAnimation] = useState(false);

  const fetchRequestDetails = async (requestType: string, requestId: string) => {
    try {
      setIsLoadingDetails(true);
      setDetailsError('');
      
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const url = `${REQUEST_DETAILS_API_URL}/${requestType}/${requestId}`;
      console.log('Fetching request details:', url);

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to fetch request details.`);
      }

      const data = await response.json();
      setRequestDetails(data);
    } catch (err: any) {
      console.error('Error fetching request details:', err);
      setDetailsError(err?.message || 'Failed to load request details.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const confirmAndReceive = async (requestId: string) => {
    try {
      setIsConfirming(true);
      setConfirmError('');
      setConfirmMessage('');
      
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Determine the correct API endpoint based on request type
      let url;
      let successMessage;
      
      if (requestDetails?.action === 'inventory_edit') {
        // For edit_inventory, use the inventory edit API
        url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/edit-inventory/${requestId}`;
        successMessage = 'Inventory edit confirmed successfully!';
      } else {
        // Default behavior for other request types
        url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending/${requestId}/confirm-and-receive`;
        successMessage = 'Request confirmed and received successfully!';
      }
      
      console.log('Confirming request:', url);

      const method = requestDetails?.action === 'inventory_edit' ? 'PUT' : 'POST';
      
      // Prepare request body for inventory_edit
      let body = null;
      if (requestDetails?.action === 'inventory_edit') {
        try {
          const payload = typeof requestDetails.payload === 'string' 
            ? JSON.parse(requestDetails.payload.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false'))
            : requestDetails.payload;
          
          body = JSON.stringify(payload?.inventory?.new_data || {});
        } catch (error) {
          console.error('Error parsing payload for inventory edit:', error);
          body = JSON.stringify({});
        }
      }
      
      const response = await fetch(url, {
        method,
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`,
        },
        body
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to confirm request.`);
      }

      const data = await response.json();
      setConfirmMessage(successMessage);
      
      // Show processing animation before auto-delete
      setTimeout(() => {
        setShowProcessingAnimation(true);
        
        // Auto-delete after animation
        setTimeout(() => {
          if (selectedRequest) {
            deleteRequest(selectedRequest.request_id);
          }
        }, 2000);
      }, 1500);
      
    } catch (err: any) {
      console.error('Error confirming request:', err);
      setConfirmError(err?.message || 'Failed to confirm request.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setConfirmError('');
      }, 5000);
    } finally {
      setIsConfirming(false);
    }
  };

  const deleteRequest = async (requestId: string) => {
    try {
      setIsDeleting(true);
      setDeleteError('');
      setDeleteMessage('');

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const url = `${REQUEST_DELETE_API_BASE_URL}/${selectedRequest?.reqtype}/${requestId}`;
      console.log('Deleting request:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to delete request.`);
      }

      setDeleteMessage('Request deleted successfully!');
      setShowDeleteConfirm(false);

      // Close modal and refresh after successful deletion
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);

    } catch (err: any) {
      console.error('Error deleting request:', err);
      setDeleteError(err?.message || 'Failed to delete request.');
      setShowDeleteConfirm(false);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setDeleteError('');
      }, 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (selectedRequest) {
      fetchRequestDetails(selectedRequest.reqtype, selectedRequest.request_id);
    } else {
      setRequestDetails(null);
      setDetailsError('');
    }
  }, [selectedRequest]);

  if (!selectedRequest) return null;

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Modal Header */}
      <div style={{
        padding: '1.5rem',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* Back Button - Left Corner */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          borderRadius: '8px',
          padding: '0.25rem',
          marginRight: '1rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              color: '#10b981',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              minWidth: '40px'
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLButtonElement;
              const parentDiv = target.parentElement as HTMLDivElement;
              if (target && parentDiv) {
                target.style.color = '#059669';
                target.style.transform = 'translateY(-2px) scale(1.05)';
                target.style.minWidth = '90px';
                parentDiv.style.background = 'rgba(16, 185, 129, 0.2)';
                parentDiv.style.transform = 'translateY(-1px)';
                
                // Animate the text appearance
                const textElement = target.querySelector('.back-text') as HTMLSpanElement;
                if (textElement) {
                  textElement.style.opacity = '1';
                  textElement.style.transform = 'translateX(0)';
                }
              }
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLButtonElement;
              const parentDiv = target.parentElement as HTMLDivElement;
              if (target && parentDiv) {
                target.style.color = '#10b981';
                target.style.transform = 'translateY(0) scale(1)';
                target.style.minWidth = '40px';
                parentDiv.style.background = 'rgba(16, 185, 129, 0.1)';
                parentDiv.style.transform = 'translateY(0)';
                
                // Animate the text disappearance
                const textElement = target.querySelector('.back-text') as HTMLSpanElement;
                if (textElement) {
                  textElement.style.opacity = '0';
                  textElement.style.transform = 'translateX(-10px)';
                }
              }
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
            <span 
              className="back-text"
              style={{
                opacity: '0',
                transform: 'translateX(-10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                position: 'absolute',
                left: '32px'
              }}
            >
              Back
            </span>
          </button>
        </div>

        {/* Header Content */}
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            Request Details
          </h3>
          <p style={{
            margin: '0.25rem 0 0',
            fontSize: '0.875rem',
            color: '#6b7280'
          }}>
            {selectedRequest.display_name || 'No display name'} - {selectedRequest.reqtype.replace(/_/g, ' ')}
          </p>
        </div>

        {/* Rejected Badge */}
        {(requestDetails?.status?.toLowerCase() === 'rejected' || selectedRequest?.queue_status === 'rejected') && (requestDetails?.rejected_reason || selectedRequest?.rejected_reason) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '6px',
            marginLeft: '1rem'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <div>
              <div style={{
                fontSize: '0.7rem',
                color: '#dc2626',
                fontWeight: '600'
              }}>
                Rejected
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#7f1d1d',
                wordBreak: 'break-word',
                maxWidth: '300px'
              }}>
                {requestDetails?.rejected_reason || selectedRequest?.rejected_reason}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Content */}
      <div style={{
        padding: '1.5rem',
        flex: 1,
        overflow: 'auto'
      }}>
        {isLoadingDetails && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(59, 130, 246, 0.2)',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem auto'
            }}></div>
            <div style={{ fontSize: '0.9rem' }}>Loading request details...</div>
          </div>
        )}

        {detailsError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {detailsError}
            </div>
          </div>
        )}

        {confirmMessage && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#16a34a',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              {confirmMessage}
            </div>
          </div>
        )}

        {/* Processing Animation */}
        {showProcessingAnimation && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        )}

        {confirmError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {confirmError}
            </div>
          </div>
        )}

        {deleteMessage && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#16a34a',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              {deleteMessage}
            </div>
          </div>
        )}

        {deleteError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {deleteError}
            </div>
          </div>
        )}

        {!isLoadingDetails && !detailsError && requestDetails && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Request Information */}
            <div style={{
              background: '#f9fafb',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <h4 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Request Information
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0.75rem',
                fontSize: '0.875rem'
              }}>
                <div>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Action:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#1f2937', fontWeight: '600' }}>
                    {requestDetails.action || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Status:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#1f2937', fontWeight: '600' }}>
                    {requestDetails.status || 'N/A'}
                  </span>
                </div>

                {requestDetails.status?.toLowerCase() === 'approved' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <button
                      onClick={() => confirmAndReceive(selectedRequest.request_id)}
                      disabled={isConfirming}
                      style={{
                        background: isConfirming ? '#9ca3af' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: isConfirming ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isConfirming ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Confirming...
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                          Confirm & Receive
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Delete Request Button */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      style={{
                        background: 'transparent',
                        color: '#dc2626',
                        border: '1px solid #dc2626',
                        borderRadius: '6px',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isDeleting) {
                          e.currentTarget.style.background = '#fef2f2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                      Delete Request
                    </button>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        Are you sure?
                      </span>
                      <button
                        onClick={() => deleteRequest(selectedRequest.request_id)}
                        disabled={isDeleting}
                        style={{
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: isDeleting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isDeleting ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                            Yes, Delete
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        style={{
                          background: 'transparent',
                          color: '#6b7280',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: isDeleting ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Reference ID:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#1f2937', fontWeight: '600' }}>
                    {requestDetails.reference_id || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Created At:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#1f2937', fontWeight: '600' }}>
                    {requestDetails.created_at ? new Date(Number(requestDetails.created_at) * 1000).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Payload Data */}
            {requestDetails.payload && (
              <div>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Payload Data
                </h4>
                {(() => {
                  try {
                    const payload = typeof requestDetails.payload === 'string' 
                      ? JSON.parse(requestDetails.payload.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false'))
                      : requestDetails.payload;
                    
                    // Handle inventory_edit action specifically
                    if (requestDetails.action === 'inventory_edit' && payload?.inventory) {
                      const { old_data, new_data } = payload.inventory;
                      return (
                        <div style={{
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            padding: '1rem',
                            background: '#f9fafb',
                            borderBottom: '1px solid #e5e7eb',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            Inventory Changes Comparison
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '0',
                            border: '1px solid #e5e7eb'
                          }}>
                            {/* Old Data Column */}
                            <div style={{
                              borderRight: '1px solid #e5e7eb'
                            }}>
                              <div style={{
                                padding: '0.75rem',
                                background: '#fef2f2',
                                borderBottom: '1px solid #e5e7eb',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#dc2626',
                                textAlign: 'center'
                              }}>
                                Previous Data
                              </div>
                              <div style={{
                                padding: '1rem',
                                fontSize: '0.8rem',
                                color: '#374151'
                              }}>
                                {Object.entries(old_data).map(([key, value]: [string, any]) => (
                                  <div key={key} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '0.5rem 0',
                                    borderBottom: '1px solid #f3f4f6'
                                  }}>
                                    <span style={{ 
                                      fontWeight: '500',
                                      color: '#6b7280',
                                      textTransform: 'capitalize',
                                      fontSize: '0.75rem'
                                    }}>
                                      {key.replace(/_/g, ' ')}:
                                    </span>
                                    <span style={{ 
                                      fontWeight: '600',
                                      color: '#1f2937',
                                      fontSize: '0.8rem'
                                    }}>
                                      {(() => {
                                      if (typeof value === 'number') {
                                        if (key.includes('price') || key.includes('cost') || key.includes('mrp')) {
                                          return `Rs ${value}`;
                                        }
                                        return value.toString();
                                      }
                                      return value || 'N/A';
                                    })()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* New Data Column */}
                            <div>
                              <div style={{
                                padding: '0.75rem',
                                background: '#f0fdf4',
                                borderBottom: '1px solid #e5e7eb',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#16a34a',
                                textAlign: 'center'
                              }}>
                                Updated Data
                              </div>
                              <div style={{
                                padding: '1rem',
                                fontSize: '0.8rem',
                                color: '#374151'
                              }}>
                                {Object.entries(new_data).map(([key, value]: [string, any]) => {
                                  const oldValue = old_data[key];
                                  const isChanged = oldValue !== value;
                                  return (
                                    <div key={key} style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      padding: '0.5rem 0',
                                      borderBottom: '1px solid #f3f4f6',
                                      background: isChanged ? 'rgba(34, 197, 94, 0.05)' : 'transparent'
                                    }}>
                                      <span style={{ 
                                        fontWeight: '500',
                                        color: '#6b7280',
                                        textTransform: 'capitalize',
                                        fontSize: '0.75rem'
                                      }}>
                                        {key.replace(/_/g, ' ')}:
                                      </span>
                                      <span style={{ 
                                        fontWeight: isChanged ? '700' : '600',
                                        color: isChanged ? '#16a34a' : '#1f2937',
                                        fontSize: '0.8rem'
                                      }}>
                                        {(() => {
                                      if (typeof value === 'number') {
                                        if (key.includes('price') || key.includes('cost') || key.includes('mrp')) {
                                          return `Rs ${value}`;
                                        }
                                        return value.toString();
                                      }
                                      return value || 'N/A';
                                    })()}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          
                          <div style={{
                            padding: '0.75rem',
                            background: '#f8fafc',
                            borderTop: '1px solid #e5e7eb',
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            textAlign: 'center',
                            fontStyle: 'italic'
                          }}>
                            {Object.keys(new_data).filter(key => old_data[key] !== new_data[key]).length} field(s) modified
                          </div>
                        </div>
                      );
                    }
                    
                    // Handle direct purchase data format (new format)
                    let medicines = [];
                    let purchaseData = null;
                    
                    if (payload?.items && Array.isArray(payload.items)) {
                      // New format: purchase data is directly in payload
                      medicines = payload.items;
                      purchaseData = payload;
                    } else if (payload?.inventory?.new_data?.items) {
                      // Old format: nested under inventory.new_data
                      medicines = payload.inventory.new_data.items;
                      purchaseData = payload.inventory.new_data;
                    } else if (Array.isArray(payload)) {
                      // Direct array format
                      medicines = payload;
                      purchaseData = { items: payload };
                    }
                    
                    if (medicines.length > 0) {
                      return (
                        <div style={{
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          {/* Scrollable Table Container */}
                          <div 
                            className="modern-scrollbar"
                            style={{
                              overflowX: 'auto',
                              overflowY: 'auto',
                              maxHeight: '400px',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e1 #f1f5f9',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              transition: 'box-shadow 0.3s ease'
                            }}
                          >
                            {/* Table Header */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(16, minmax(100px, 1fr))',
                              gap: '0.5rem',
                              padding: '0.75rem 1rem',
                              background: '#f9fafb',
                              borderBottom: '1px solid #e5e7eb',
                              fontSize: '0.65rem',
                              fontWeight: '600',
                              color: '#6b7280',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              position: 'sticky',
                              top: 0,
                              zIndex: 10
                            }}>
                              <div>Medicine Name</div>
                              <div>Company</div>
                              <div>Composition</div>
                              <div>Batch No</div>
                              <div>Expiry Date</div>
                              <div>Quantity</div>
                              <div>Free</div>
                              <div>Type</div>
                              <div>Tablets/Strip</div>
                              <div>Unit Cost</div>
                              <div>MRP</div>
                              <div>Selling Price</div>
                              <div>HSN Code</div>
                              <div>Discount %</div>
                              <div>Tax %</div>
                              <div>Line Total</div>
                            </div>
                            
                            {/* Table Rows */}
                            {medicines.map((medicine: any, index: number) => (
                              <div
                                key={medicine.id || index}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(16, minmax(100px, 1fr))',
                                  gap: '0.5rem',
                                  padding: '0.75rem 1rem',
                                  borderBottom: index < medicines.length - 1 ? '1px solid #f3f4f6' : 'none',
                                  background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                  fontSize: '0.75rem',
                                  alignItems: 'center',
                                  minWidth: '1600px'
                                }}
                              >
                                <div style={{ 
                                  color: '#374151', 
                                  fontWeight: '600',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.medicine_name || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.medicine_company || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.medicine_composition || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.batch_no || medicine.batch_number || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  fontSize: '0.7rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.expiry_date || medicine.expiry || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#1f2937',
                                  fontWeight: '500',
                                  textAlign: 'center'
                                }}>
                                  {medicine.quantity || 0}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  textAlign: 'center'
                                }}>
                                  {medicine.free || 0}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.quantity_type || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  textAlign: 'center'
                                }}>
                                  {medicine.tablets_per_strip || 1}
                                </div>
                                <div style={{ 
                                  color: '#1f2937',
                                  fontWeight: '500',
                                  textAlign: 'right'
                                }}>
                                  {medicine.unit_cost ? `Rs ${medicine.unit_cost}` : 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#059669',
                                  fontWeight: '600',
                                  textAlign: 'right'
                                }}>
                                  {medicine.mrp ? `Rs ${medicine.mrp}` : 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#1f2937',
                                  fontWeight: '500',
                                  textAlign: 'right'
                                }}>
                                  {medicine.selling_price_per_unit ? `Rs ${medicine.selling_price_per_unit}` : 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {medicine.hsn_code || 'N/A'}
                                </div>
                                <div style={{ 
                                  color: '#dc2626',
                                  fontWeight: '500',
                                  textAlign: 'right'
                                }}>
                                  {medicine.discount_percent || 0}%
                                </div>
                                <div style={{ 
                                  color: '#7c3aed',
                                  fontWeight: '500',
                                  textAlign: 'right'
                                }}>
                                  {medicine.tax_percent || medicine.gst_percent || 0}%
                                </div>
                                <div style={{ 
                                  color: '#1f2937',
                                  fontWeight: '600',
                                  textAlign: 'right'
                                }}>
                                  {medicine.line_total ? `Rs ${medicine.line_total}` : 'N/A'}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Summary Row */}
                          <div style={{
                            padding: '1rem',
                            background: '#f3f4f6',
                            borderTop: '2px solid #e5e7eb',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#1f2937',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            bottom: 0
                          }}>
                            <div>Total Items: {medicines.length}</div>
                            <div>
                              Total Amount: {purchaseData?.amount_without_tax || purchaseData?.total_amount ? 
                                `Rs ${purchaseData.amount_without_tax || purchaseData.total_amount}` : 
                                `Rs ${medicines.reduce((sum: number, med: any) => sum + (med.line_total || 0), 0)}`
                              }
                            </div>
                            <div>
                              Tax Amount: {purchaseData?.tax_amount ? 
                                `Rs ${purchaseData.tax_amount}` : 
                                `Rs ${medicines.reduce((sum: number, med: any) => sum + (med.tax_amount || 0), 0)}`
                              }
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Fallback for other payload types
                    return (
                      <div style={{
                        background: '#f3f4f6',
                        borderRadius: '8px',
                        padding: '1rem',
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(payload, null, 2)}
                      </div>
                    );
                  } catch (error) {
                    return (
                      <div style={{
                        background: '#f3f4f6',
                        borderRadius: '8px',
                        padding: '1rem',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        Unable to parse payload data
                      </div>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Modern Scrollbar Styling */
        .modern-scrollbar::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }

        .modern-scrollbar::-webkit-scrollbar-track {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
        }

        .modern-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
          border-radius: 10px;
          border: 2px solid #f8fafc;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        .modern-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #64748b 0%, #475569 100%);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          transform: translateY(-1px);
        }

        .modern-scrollbar::-webkit-scrollbar-thumb:active {
          background: linear-gradient(135deg, #475569 0%, #334155 100%);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
          transform: translateY(0);
        }

        .modern-scrollbar::-webkit-scrollbar-corner {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border-radius: 0 0 10px 0;
        }

        /* Firefox scrollbar styling */
        .modern-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #64748b #f1f5f9;
        }

        /* Smooth scrolling behavior */
        .modern-scrollbar {
          scroll-behavior: smooth;
        }

        /* Hover effects for scrollable area */
        .modern-scrollbar:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Custom scrollbar for mobile */
        @media (max-width: 768px) {
          .modern-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .modern-scrollbar::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
            border: 1px solid #f8fafc;
          }
        }
      `}</style>
    </div>
  );
};

export default RequestedItems;
